import { z } from 'zod'

import { filterFor, serializeOrderStatuses, type OrderTabKey } from '@/features/group/order-tabs'
import type { GroupOrder } from '@/features/group/schema'
import { orderHasUnclearProduct } from '@/features/group/order-status-badge'
import { orderListTabCounts } from '@/features/order-list/schema'
import type {
  OrderListFilters,
  OrderListStatusCounts,
  OrderListFilterGroup,
} from '@/features/order-list/schema'

export const orderManagementSearchSchema = z.object({
  tab: z.enum(['unpaid', 'billed', 'processing', 'shipped', 'done']).catch('unpaid'),
  search: z.string().catch(''),
  source: z.string().catch(''),
  group: z.string().catch(''),
  startDate: z.union([z.iso.date(), z.literal('')]).optional().catch(''),
  endDate: z.union([z.iso.date(), z.literal('')]).optional().catch(''),
})
export type OrderManagementSearch = z.infer<typeof orderManagementSearchSchema>
export const defaultOrderManagementSearch = orderManagementSearchSchema.parse({})

export function orderManagementFilters(search: OrderManagementSearch): OrderListFilters {
  return {
    orderStatus:
      search.tab === 'billed'
        ? 'UNPAID,BILLED'
        : serializeOrderStatuses(filterFor(search.tab).orderStatuses),
    ...(search.tab === 'unpaid'
      ? { hasUnclearProduct: true }
      : search.tab === 'billed'
        ? { hasUnclearProduct: false }
        : {}),
    search: search.search.trim() || undefined,
    sources: search.source || undefined,
    groups: search.group || undefined,
    ...(search.startDate ? { startDate: search.startDate } : {}),
    ...(search.endDate ? { endDate: search.endDate } : {}),
  }
}

export function listTabCounts(counts: OrderListStatusCounts): Record<OrderTabKey, number> {
  return orderListTabCounts(counts)
}

export function groupOrdersByOwner(orders: readonly GroupOrder[]) {
  const groups = new Map<string, GroupOrder[]>()
  for (const order of orders) {
    if (!order.groupId) continue
    const group = groups.get(order.groupId) ?? []
    group.push(order)
    groups.set(order.groupId, group)
  }
  return groups
}

export function canDeleteListedOrder(order: GroupOrder) {
  return isAwaitingConfirmation(order) && Boolean(order.groupId)
}

export function isAwaitingConfirmation(order: GroupOrder) {
  return order.status === 'UNPAID' && orderHasUnclearProduct(order)
}

export function isAwaitingPayment(order: GroupOrder) {
  return (order.status === 'UNPAID' && !orderHasUnclearProduct(order)) || order.status === 'BILLED'
}

export function canSelectListedOrder(order: GroupOrder) {
  return canDeleteListedOrder(order) || (order.status === 'IN_PROCESS' && Boolean(order.groupId))
}

export function orderSourceLabel(source: string) {
  if (source === 'WHATSAPP') return 'Grup WhatsApp'
  if (source === 'MANUAL') return 'Manual'
  if (source === 'AI') return 'Rekap AI'
  return source || 'Sumber tidak tersedia'
}

export function orderGroupLabel(
  id: string,
  groups: readonly OrderListFilterGroup[],
  pending = false,
) {
  return (
    groups.find((group) => group.id === id)?.name?.trim() ||
    (pending ? 'Memuat nama grup…' : 'Grup tidak tersedia')
  )
}

export function activeOrderFilters(
  search: OrderManagementSearch,
  groups: readonly OrderListFilterGroup[],
  pending = false,
) {
  const filters: { key: 'source' | 'group' | 'search' | 'startDate' | 'endDate'; label: string }[] = []
  if (search.source)
    filters.push({ key: 'source', label: `Sumber: ${orderSourceLabel(search.source)}` })
  if (search.group)
    filters.push({ key: 'group', label: `Grup: ${orderGroupLabel(search.group, groups, pending)}` })
  if (search.search.trim()) filters.push({ key: 'search', label: `Cari: ${search.search.trim()}` })
  if (search.startDate) filters.push({ key: 'startDate', label: `Dari: ${search.startDate}` })
  if (search.endDate) filters.push({ key: 'endDate', label: `Sampai: ${search.endDate}` })
  return filters
}
